import { useMemo, FunctionComponent, MouseEventHandler } from "react";
import classNames from "classnames";
import { RatingComponent } from "./RatingComponent";
import { usePreferences } from "../contexts/PreferencesContext";
import { AppItem } from "@app/types";
import { useEvent } from "@app/hooks/useEvent";
import { useAuth } from "@app/contexts/AuthContext";

type ItemProps = {
  current: boolean;
  item: AppItem;
  onLocationChange: (id: string) => void;
  onChange?: (item: AppItem) => void;
  onTagSelected?: (data: { item: AppItem; tag: string }) => void;
};

/**
 * One name in the sidebar list.
 *
 * The name stays an anchor pointing at the search it would run, so a middle click
 * still opens that search in a new tab; a plain click is intercepted and selects the
 * item here instead.
 */
export const Item: FunctionComponent<ItemProps> = ({
  current,
  item,
  onLocationChange,
  onChange,
  onTagSelected,
}) => {
  const auth = useAuth();
  const { getLink } = usePreferences();

  const link = useMemo(() => getLink(item.name), [getLink, item.name]);

  const handleRatingChange = (rating: number | null) => {
    if (auth.state !== "authenticated") return;
    onChange?.({ ...item, rating });
  };

  const handleAuxClick = useEvent<MouseEventHandler<HTMLAnchorElement>>((e) => {
    if (e.nativeEvent.which !== 2) return;
    onLocationChange(item.id);
  });

  return (
    <div
      className={classNames(
        /*
          `px-1.5` against the list's own `px-1.5` puts the name at 12px from the
          sidebar edge — the rail the filter and the sort control already sit on. At
          `px-2` it was 14, and the highlight bled asymmetrically around it.
        */
        "rounded-lg px-1.5 py-1.5 transition-colors",
        current ? "bg-accent/10" : "hover:bg-surface-raised"
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <a
          href={link}
          aria-current={current ? "true" : undefined}
          className={classNames(
            /*
              `leading-6` matches the rating's cell height exactly. Without it the
              name's 19px line box sat inside a 24px row and centring pushed the
              difference above it, so the highlight on the selected item had 8.4px of
              padding at the top against 6px at the bottom.
            */
            "min-w-0 flex-1 text-[0.9375rem] leading-6 no-underline md:text-sm",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
            current ? "font-semibold text-accent" : "font-medium text-ink"
          )}
          onClick={(e) => {
            e.preventDefault();
            onLocationChange(item.id);
          }}
          onAuxClick={handleAuxClick}
        >
          {item.name}
        </a>
        {/*
          Ratings are shown for everyone and only pressable when signed in — a guest
          seeing an empty row of stars they cannot fill is worse than seeing none.
        */}
        <div className="shrink-0">
          <RatingComponent
            score={item.rating}
            onChange={
              auth.state === "authenticated" ? handleRatingChange : undefined
            }
          />
        </div>
      </div>
      {item.tags.length > 0 && (
        <ul className="mt-1 flex flex-row flex-wrap gap-1">
          {item.tags.map((tag) => (
            <li key={tag}>
              {/*
                A button, not a bare <li onClick>: filtering by a tag is an action, and
                it was previously reachable with a mouse and by no other means.
              */}
              <button
                type="button"
                onClick={() => onTagSelected?.({ item, tag })}
                title={`Filter by "${tag}"`}
                /*
                  `bg-line` rather than `bg-surface-sunken`: the sidebar *is* the sunken
                  surface, so a chip painted with it is invisible, and the row's hover
                  state is the raised one, so a raised chip disappears on hover instead.
                  The rule colour is the one shade that reads against both.
                */
                className="cursor-pointer rounded-full bg-line px-1.5 py-0.5
                  text-micro text-ink-muted transition-colors hover:bg-accent
                  hover:text-accent-ink focus-visible:outline-2
                  focus-visible:outline-offset-1 focus-visible:outline-accent"
              >
                {tag}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
